// User preferences and settings management utility
// Manages user preferences for tours, UI settings, and other personalization options

const STORAGE_KEY = "trackingTool_userPreferences";

// Default user preferences
const DEFAULT_PREFERENCES = {
	tours: {
		enabled: true,
		autoStart: false, // Changed to false to disable auto-popup tours
		showWelcome: true,
	},
	ui: {
		sidebarCollapsed: false,
		itemsPerPage: 25,
		theme: "default",
	},
	notifications: {
		showUpdateMessages: true,
		showTips: true,
	},
};

// Get all user preferences from localStorage
export const getUserPreferences = () => {
	try {
		const prefs = localStorage.getItem(STORAGE_KEY);
		if (prefs) {
			const parsed = JSON.parse(prefs);
			// Merge with defaults to ensure all properties exist
			return { ...DEFAULT_PREFERENCES, ...parsed };
		}
		return DEFAULT_PREFERENCES;
	} catch (error) {
		console.error("Error reading user preferences from localStorage:", error);
		return DEFAULT_PREFERENCES;
	}
};

// Save user preferences to localStorage
export const saveUserPreferences = (preferences) => {
	try {
		const currentPrefs = getUserPreferences();
		const updatedPrefs = { ...currentPrefs, ...preferences };
		localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedPrefs));
		return true;
	} catch (error) {
		console.error("Error saving user preferences to localStorage:", error);
		return false;
	}
};

// Get a specific preference value
export const getPreference = (category, key) => {
	const prefs = getUserPreferences();
	return prefs[category]?.[key];
};

// Set a specific preference value
export const setPreference = (category, key, value) => {
	const prefs = getUserPreferences();
	if (!prefs[category]) {
		prefs[category] = {};
	}
	prefs[category][key] = value;
	return saveUserPreferences(prefs);
};

// Tour-specific preference helpers
export const isTourEnabled = () => getPreference("tours", "enabled");
export const shouldAutoStartTours = () => getPreference("tours", "autoStart");
export const shouldShowWelcome = () => getPreference("tours", "showWelcome");

export const setTourEnabled = (enabled) =>
	setPreference("tours", "enabled", enabled);
export const setAutoStartTours = (autoStart) =>
	setPreference("tours", "autoStart", autoStart);
export const setShowWelcome = (show) =>
	setPreference("tours", "showWelcome", show);

// UI-specific preference helpers
export const isSidebarCollapsed = () => getPreference("ui", "sidebarCollapsed");
export const getItemsPerPage = () => getPreference("ui", "itemsPerPage");
export const getTheme = () => getPreference("ui", "theme");

export const setSidebarCollapsed = (collapsed) =>
	setPreference("ui", "sidebarCollapsed", collapsed);
export const setItemsPerPage = (count) =>
	setPreference("ui", "itemsPerPage", count);
export const setTheme = (theme) => setPreference("ui", "theme", theme);

// Reset all preferences to defaults
export const resetUserPreferences = () => {
	try {
		localStorage.removeItem(STORAGE_KEY);
		return true;
	} catch (error) {
		console.error("Error resetting user preferences:", error);
		return false;
	}
};

// Export preferences for backup/restore
export const exportUserPreferences = () => {
	const prefs = getUserPreferences();
	return JSON.stringify(prefs, null, 2);
};

// Import preferences from backup
export const importUserPreferences = (preferencesJson) => {
	try {
		const prefs = JSON.parse(preferencesJson);
		return saveUserPreferences(prefs);
	} catch (error) {
		console.error("Error importing user preferences:", error);
		return false;
	}
};
